import { useState, useEffect } from "react";
import { useSnapshot } from "valtio";
import { toast } from "sonner";
import {
  adminStore,
  fetchFeatureFlags,
  type FeatureFlag,
} from "@/features/admin/stores/admin";
import { upsertFeatureFlag } from "@/lib/instantdb";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Flag,
  Plus,
  Pencil,
  Trash2,
  Users,
  Percent,
  AlertCircle,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { tx, transact } from "@/lib/instantdb";

interface FlagFormData {
  name: string;
  description: string;
  enabled: boolean;
  rolloutPercent: number;
  allowedUserIds: string;
  blockedUserIds: string;
}

const defaultFormData: FlagFormData = {
  name: "",
  description: "",
  enabled: false,
  rolloutPercent: 100,
  allowedUserIds: "",
  blockedUserIds: "",
};

function FlagForm({
  initialData,
  onSubmit,
  onCancel,
  isEdit = false,
}: {
  initialData?: Partial<FlagFormData>;
  onSubmit: (data: FlagFormData) => void;
  onCancel: () => void;
  isEdit?: boolean;
}) {
  const [formData, setFormData] = useState<FlagFormData>({
    ...defaultFormData,
    ...initialData,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Flag Name</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="new-timeline-editor"
          disabled={isEdit}
          required
        />
        <p className="text-xs text-muted-foreground">
          Use kebab-case (e.g., new-feature, beta-dashboard)
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Input
          id="description"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          placeholder="Enable the new timeline editor experience"
          required
        />
      </div>

      <div className="flex items-center justify-between rounded-lg border p-4">
        <div className="space-y-0.5">
          <Label>Enabled</Label>
          <p className="text-sm text-muted-foreground">
            Toggle this flag on or off globally
          </p>
        </div>
        <Switch
          checked={formData.enabled}
          onCheckedChange={(checked) =>
            setFormData({ ...formData, enabled: checked })
          }
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="rollout">Rollout Percentage</Label>
        <div className="flex items-center gap-3">
          <Input
            id="rollout"
            type="number"
            min={0}
            max={100}
            value={formData.rolloutPercent}
            onChange={(e) =>
              setFormData({
                ...formData,
                rolloutPercent: parseInt(e.target.value) || 0,
              })
            }
            className="w-24"
          />
          <span className="text-muted-foreground">%</span>
          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${formData.rolloutPercent}%` }}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Percentage of users who will see this feature (deterministic per user)
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="allowed">Allowed User IDs (Beta Testers)</Label>
        <Input
          id="allowed"
          value={formData.allowedUserIds}
          onChange={(e) =>
            setFormData({ ...formData, allowedUserIds: e.target.value })
          }
          placeholder="user1@example.com, user2@example.com"
        />
        <p className="text-xs text-muted-foreground">
          Comma-separated list of user IDs who always see this flag
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="blocked">Blocked User IDs</Label>
        <Input
          id="blocked"
          value={formData.blockedUserIds}
          onChange={(e) =>
            setFormData({ ...formData, blockedUserIds: e.target.value })
          }
          placeholder="user3@example.com"
        />
        <p className="text-xs text-muted-foreground">
          Comma-separated list of user IDs who never see this flag
        </p>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">{isEdit ? "Update" : "Create"} Flag</Button>
      </DialogFooter>
    </form>
  );
}

export default function FeatureFlagsPage() {
  const { featureFlags, featureFlagsLoading, error } = useSnapshot(adminStore);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [flagToDelete, setFlagToDelete] = useState<FeatureFlag | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchFeatureFlags();
  }, []);

  const flags = featureFlags as FeatureFlag[];

  const handleCreateFlag = async (formData: FlagFormData) => {
    const flagId = crypto.randomUUID();
    await upsertFeatureFlag(flagId, {
      name: formData.name,
      description: formData.description,
      enabled: formData.enabled,
      rolloutPercent: formData.rolloutPercent,
      allowedUserIds: formData.allowedUserIds
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      blockedUserIds: formData.blockedUserIds
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      metadata: {},
      createdAt: Date.now(),
    });
    setDialogOpen(false);
    toast.success("Feature flag created");
    fetchFeatureFlags(true); // Force refresh
  };

  const handleUpdateFlag = async (formData: FlagFormData) => {
    if (!editingFlag) return;
    await upsertFeatureFlag(editingFlag.id, {
      description: formData.description,
      enabled: formData.enabled,
      rolloutPercent: formData.rolloutPercent,
      allowedUserIds: formData.allowedUserIds
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      blockedUserIds: formData.blockedUserIds
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    });
    setEditingFlag(null);
    toast.success("Feature flag updated");
    fetchFeatureFlags(true); // Force refresh
  };

  const handleToggleFlag = async (flag: FeatureFlag) => {
    await upsertFeatureFlag(flag.id, { enabled: !flag.enabled });
    toast.success(`Feature flag ${flag.enabled ? "disabled" : "enabled"}`);
    fetchFeatureFlags(true); // Force refresh
  };

  const handleDeleteFlag = async () => {
    if (!flagToDelete) return;
    setDeleteLoading(true);
    try {
      await transact(tx.feature_flags[flagToDelete.id].delete());
      toast.success("Feature flag deleted");
      setDeleteDialogOpen(false);
      setFlagToDelete(null);
      fetchFeatureFlags(true); // Force refresh
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete flag");
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDeleteDialog = (flag: FeatureFlag) => {
    setFlagToDelete(flag);
    setDeleteDialogOpen(true);
  };

  const openEditDialog = (flag: FeatureFlag) => {
    setEditingFlag(flag);
  };

  return (
    <div className="py-6">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Feature Flags</h1>
            <p className="mt-2 text-muted-foreground">
              Control feature rollout and A/B testing
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                New Flag
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create Feature Flag</DialogTitle>
                <DialogDescription>
                  Create a new feature flag to control feature visibility.
                </DialogDescription>
              </DialogHeader>
              <FlagForm
                onSubmit={handleCreateFlag}
                onCancel={() => setDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>

        {/* Error State */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{String(error)}</AlertDescription>
          </Alert>
        )}

        {/* Flags Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Flag className="h-5 w-5" />
              Feature Flags ({flags.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {featureFlagsLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : flags.length === 0 ? (
              <div className="text-center py-8">
                <Flag className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-semibold">No feature flags</h3>
                <p className="mt-2 text-muted-foreground">
                  Create your first feature flag to start controlling features.
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Flag</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Rollout</TableHead>
                    <TableHead>Users</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {flags.map((flag) => {
                    const allowedCount =
                      (flag.allowedUserIds as string[] | null)?.length ?? 0;
                    const blockedCount =
                      (flag.blockedUserIds as string[] | null)?.length ?? 0;
                    return (
                      <TableRow key={flag.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium font-mono">{flag.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {flag.description}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={flag.enabled}
                            onCheckedChange={() => handleToggleFlag(flag)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Percent className="h-4 w-4 text-muted-foreground" />
                            <span>{flag.rolloutPercent}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {allowedCount > 0 && (
                              <Badge variant="secondary">
                                <Users className="mr-1 h-3 w-3" />
                                {allowedCount} allowed
                              </Badge>
                            )}
                            {blockedCount > 0 && (
                              <Badge variant="outline">
                                {blockedCount} blocked
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(flag)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openDeleteDialog(flag)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={!!editingFlag} onOpenChange={() => setEditingFlag(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Feature Flag</DialogTitle>
              <DialogDescription>
                Update the configuration for{" "}
                <code className="font-mono">{editingFlag?.name}</code>
              </DialogDescription>
            </DialogHeader>
            {editingFlag && (
              <FlagForm
                initialData={{
                  name: editingFlag.name,
                  description: editingFlag.description,
                  enabled: editingFlag.enabled,
                  rolloutPercent: editingFlag.rolloutPercent,
                  allowedUserIds:
                    (editingFlag.allowedUserIds as string[] | null)?.join(
                      ", ",
                    ) ?? "",
                  blockedUserIds:
                    (editingFlag.blockedUserIds as string[] | null)?.join(
                      ", ",
                    ) ?? "",
                }}
                onSubmit={handleUpdateFlag}
                onCancel={() => setEditingFlag(null)}
                isEdit
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title="Delete Feature Flag"
          description={`Are you sure you want to delete the feature flag "${flagToDelete?.name}"? This action cannot be undone.`}
          confirmLabel="Delete"
          variant="destructive"
          onConfirm={handleDeleteFlag}
          loading={deleteLoading}
        />
      </div>
    </div>
  );
}
